import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button, Grid, Divider } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ListAltIcon from "@mui/icons-material/ListAlt";
import { fetchActionPlan, deleteActionPlan } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import DetailSection from "../../components/common/DetailSection";
import DetailField from "../../components/common/DetailField";
import ConfirmDialog from "../../components/common/ConfirmDialog";

export default function ActionPlanDetailPage() {
  const { businessDomainId, businessUnitId, actionPlanId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const basePath = `/strategy/${businessDomainId}/business-units/${businessUnitId}/action-plans/${actionPlanId}`;

  const [data, setData] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loadData = useCallback(async () => {
    try { setData(await fetchActionPlan(actionPlanId)); }
    catch (e) { notify.apiError(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionPlanId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDelete = async () => {
    try {
      await deleteActionPlan(actionPlanId);
      notify.success(t("message.delete.success"));
      navigate(`/strategy/${businessDomainId}/business-units/${businessUnitId}/action-plans`, { replace: true });
    } catch (e) { notify.apiError(e); }
    setConfirmOpen(false);
  };

  if (!data) return null;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("actionPlan.detail.title")}</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`${basePath}/edit`)}
          >
            {t("common.edit")}
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setConfirmOpen(true)}
          >
            {t("common.delete")}
          </Button>
        </Box>
      </Box>

      <DetailSection>
        <Grid container spacing={2}>
          <Grid size={12}>
            <DetailField label={t("actionPlan.name")} value={data.actionPlanName} />
          </Grid>
          <Grid size={12}>
            <DetailField label={t("common.description")} value={data.actionPlanDescription} />
          </Grid>
          <Grid size={12}>
            <DetailField label={t("actionPlan.type")} value={t(`actionPlanType.${data.actionPlanType || "None"}`)} />
          </Grid>
          <Grid size={12}>
            <DetailField label={t("actionPlan.businessProcess")} value={data.businessProcessName} />
          </Grid>
          <Grid size={12}>
            <DetailField label={t("actionPlan.businessUnit")} value={data.businessUnitName} />
          </Grid>
        </Grid>
      </DetailSection>

      <Divider sx={{ my: 3 }} />

      {/* 関連セクションへのナビゲーション */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Button
          variant="outlined"
          startIcon={<ListAltIcon />}
          onClick={() => navigate(`${basePath}/actions`)}
        >
          {t("actionPlan.actions")}
        </Button>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Button variant="outlined" onClick={() => navigate(`/strategy/${businessDomainId}/business-units/${businessUnitId}/action-plans`)}>{t("common.back")}</Button>
      </Box>

      <ConfirmDialog
        open={confirmOpen}
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </Box>
  );
}
